import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { useAuth } from '@/stores/auth';
import { callCreateRequest } from '@/lib/callables';
import { compressImage } from '@/lib/image';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Select } from '@/components/ui/Input';
import { CategoryPicker } from '@/components/ui/CategoryPicker';
import { LocationPicker, type LocationValue } from '@/components/ui/LocationPicker';
import { toast } from '@/components/ui/Toast';
import {
  OPERATES_FROM_OPTIONS,
  REQUEST_DESCRIPTION_MAX_CHARS,
  REQUEST_TITLE_MAX_CHARS,
  zarCents,
  type OperatesFrom,
  type ServiceCategory,
} from '@makhe/shared';

const MAX_PHOTOS = 6;
const randId = () => Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => b.toString(16).padStart(2, '0')).join('');

export default function RequestNewPage() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  const [assetId] = useState(randId);

  const [category, setCategory] = useState<ServiceCategory[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [budget, setBudget] = useState('');
  const [flexible, setFlexible] = useState(true);
  const [preferredAt, setPreferredAt] = useState('');
  const [locType, setLocType] = useState<OperatesFrom>('buyer_location');
  const [residence, setResidence] = useState('');
  const [coord, setCoord] = useState<LocationValue | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!firebaseUser) return null;

  const onPhotos = async (files: FileList) => {
    const room = MAX_PHOTOS - photos.length;
    const picked = Array.from(files).filter((f) => f.type.startsWith('image/')).slice(0, room);
    if (!picked.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(
        picked.map(async (file) => {
          const blob = await compressImage(file);
          const r = ref(storage, `requests/${assetId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`);
          await uploadBytes(r, blob, { contentType: blob.type || 'image/jpeg' });
          return getDownloadURL(r);
        }),
      );
      setPhotos((p) => [...p, ...urls]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    const cat = category[0];
    if (!cat) return toast.error('Pick a category.');
    if (!title.trim()) return toast.error('Add a title.');
    if (uploading) return toast.error('Wait for photos to finish.');
    setSubmitting(true);
    try {
      const res = await callCreateRequest({
        category: cat,
        title: title.trim(),
        description: description.trim(),
        referenceImageURLs: photos,
        budgetHintZARcents: budget ? zarCents(parseFloat(budget)) : undefined,
        flexible,
        preferredStartMillis: !flexible && preferredAt ? new Date(preferredAt).getTime() : undefined,
        location: {
          type: locType,
          ...(coord ? { geoPoint: { latitude: coord.latitude, longitude: coord.longitude } } : {}),
          ...(coord?.address ? { address: coord.address } : {}),
          ...(residence ? { residence } : {}),
        },
      });
      toast.success('Request posted — creators in your category will be notified.');
      navigate(`/requests/${res.data.requestId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-6 py-8">
      <h1 className="font-display text-3xl">Post a request</h1>
      <p className="mt-2 text-makhe-muted">
        Can't find it in the feed? Describe what you want — creators send you offers.
      </p>

      <div className="mt-6 space-y-6">
        <Card className="space-y-4">
          <h2 className="font-display text-xl">What do you need?</h2>
          <div>
            <span className="eyebrow">Category</span>
            <div className="mt-3">
              <CategoryPicker value={category} onChange={(v) => setCategory(v.slice(-1))} multiple={false} />
            </div>
          </div>
          <Input
            label="Title"
            value={title}
            maxLength={REQUEST_TITLE_MAX_CHARS}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Bridal makeup trial"
          />
          <Textarea
            label="Details"
            rows={4}
            maxLength={REQUEST_DESCRIPTION_MAX_CHARS}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Soft glam, long-lasting, I have my own lashes. Looking for someone who can come to res."
            hint={`${description.length} / ${REQUEST_DESCRIPTION_MAX_CHARS}`}
          />
        </Card>

        <Card className="space-y-3">
          <h2 className="font-display text-xl">Reference photos</h2>
          <p className="text-sm text-makhe-muted">Show creators the look you're after. Up to {MAX_PHOTOS}.</p>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {photos.map((url) => (
                <div key={url} className="group relative aspect-square overflow-hidden rounded-xl bg-makhe-line/40">
                  <img src={url} alt="Reference" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos((p) => p.filter((u) => u !== url))}
                    className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-sm text-white"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {photos.length < MAX_PHOTOS && (
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={uploading}
              onChange={(e) => e.target.files?.length && onPhotos(e.target.files)}
              className="block w-full text-sm text-makhe-ink/70 file:mr-3 file:rounded-full file:border-0 file:bg-makhe-line/50 file:px-4 file:py-2 file:text-makhe-ink"
            />
          )}
          {uploading && <p className="text-xs text-makhe-muted">Uploading…</p>}
        </Card>

        <Card className="space-y-4">
          <h2 className="font-display text-xl">Budget & timing</h2>
          <Input
            label="Budget hint (R, optional)"
            type="number"
            min={0}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="600"
            hint="Helps creators tailor their offer. They can still bid above or below."
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={flexible} onChange={(e) => setFlexible(e.target.checked)} />
            I'm flexible on timing
          </label>
          {!flexible && (
            <Input
              label="Preferred date & time"
              type="datetime-local"
              value={preferredAt}
              onChange={(e) => setPreferredAt(e.target.value)}
            />
          )}
        </Card>

        <Card className="space-y-3">
          <h2 className="font-display text-xl">Where</h2>
          <Select
            label="Location type"
            value={locType}
            onChange={(e) => setLocType(e.target.value as OperatesFrom)}
            options={OPERATES_FROM_OPTIONS.map((o) => ({ value: o, label: o.replace('_', ' ') }))}
          />
          {locType === 'buyer_location' && (
            <Input
              label="Residence / room (optional)"
              value={residence}
              onChange={(e) => setResidence(e.target.value)}
              placeholder="Knockando, Room 314"
            />
          )}
          <LocationPicker value={coord} onChange={setCoord} />
        </Card>

        <Button className="w-full" loading={submitting} disabled={uploading} onClick={submit}>
          Post request
        </Button>
      </div>
    </section>
  );
}
